/**
 * Phase 8-H — Brand terminology helper.
 *
 * The agency-facing UI displays "Brand" instead of "Shop" by default.
 * Enterprise organisations (where `account_type === 'enterprise'`) display
 * "Business Unit" instead. Backend variable names (`shop_id`, `shop_slug`)
 * and persisted Redux fields (`accessible_shops`) are unchanged — only
 * DISPLAY strings flow through this helper.
 */
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';

export type AccountType = 'agency' | 'enterprise' | string | null | undefined;

export interface BrandLabels {
  shopSingular: string; // "Brand" or "Business Unit"
  shopPlural: string; // "Brands" or "Business Units"
  clientSingular: string; // "Brand contact" or "Team member"
  clientPlural: string; // "Brand contacts" or "Team members"
  inviteCta: string; // "Invite brand contact" or "Add team member"
  shopLowerSingular: string; // "brand" or "business unit"
  shopLowerPlural: string; // "brands" or "business units"
}

export function brandLabels(accountType: AccountType): BrandLabels {
  if (accountType === 'enterprise') {
    return {
      shopSingular: 'Business Unit',
      shopPlural: 'Business Units',
      clientSingular: 'Team member',
      clientPlural: 'Team members',
      inviteCta: 'Add team member',
      shopLowerSingular: 'business unit',
      shopLowerPlural: 'business units',
    };
  }
  return {
    shopSingular: 'Brand',
    shopPlural: 'Brands',
    clientSingular: 'Brand contact',
    clientPlural: 'Brand contacts',
    inviteCta: 'Invite brand contact',
    shopLowerSingular: 'brand',
    shopLowerPlural: 'brands',
  };
}

// Phase 8-H spec alias — `labelsFor(accountType)` matches the canonical
// brand-mapping interface from the migration plan.
export const labelsFor = brandLabels;

// Convenience: pull labels for the current agency employee identity from Redux.
export function useBrandLabels(): BrandLabels {
  const accountType = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity?.account_type
  );
  return brandLabels(accountType);
}
